<template>
  <section role="region" :aria-labelledby="titleId" class="panel gold_top_rule relative mx-auto w-full max-w-xl border border-border-strong rounded-lg bg-bg-elevated p-6 shadow-lg">
    <div v-if="session === null" class="flex flex-col gap-3">
      <h2 :id="titleId" class="font-display text-sm font-semibold tracking-wide uppercase text-text-primary">{{ messages.onboarding.loginTitle }}</h2>
      <p class="text-xs text-text-secondary leading-relaxed">{{ messages.onboarding.loginDescription }}</p>
      <AppButton class="self-start" @click="onLogin">{{ messages.onboarding.login }}</AppButton>
    </div>
    <div v-else-if="lockedGuilds.length > 0" class="flex flex-col gap-3">
      <h2 :id="titleId" class="font-display text-sm font-semibold tracking-wide uppercase text-text-primary">{{ messages.onboarding.accessTitle }}</h2>
      <p class="text-xs text-text-secondary leading-relaxed">{{ messages.onboarding.accessDescription }}</p>
      <div class="flex flex-col gap-2">
        <div v-for="lockedGuild in lockedGuilds" :key="lockedGuild.id" class="flex flex-col gap-2 rounded-md border border-border bg-bg p-3 sm:flex-row sm:items-center sm:justify-between">
          <div class="min-w-0">
            <p class="truncate text-sm font-semibold text-text-primary">{{ lockedGuild.name }}</p>
            <p class="text-xs text-text-muted">{{ lockedGuild.accessRequested ? messages.onboarding.pending : messages.onboarding.roleRequired }}</p>
          </div>
          <AppButton :disabled="lockedGuild.accessRequested || requestingGuildId === lockedGuild.discordGuildId" @click="requestAccess(lockedGuild.discordGuildId)">{{ requestButtonLabel(lockedGuild.discordGuildId, lockedGuild.accessRequested) }}</AppButton>
        </div>
      </div>
      <p v-if="accessRequestError" class="text-xs text-red-400" role="alert">{{ accessRequestError }}</p>
    </div>
    <div v-else class="flex flex-col gap-3">
      <h2 :id="titleId" class="font-display text-sm font-semibold tracking-wide uppercase text-text-primary">{{ messages.onboarding.emptyTitle }}</h2>
      <p class="text-xs text-text-secondary leading-relaxed">{{ messages.onboarding.emptyDescription }}</p>
      <p class="text-xs text-text-secondary">{{ messages.onboarding.addBotFromSelector }}</p>
      <p class="text-xs text-text-muted">{{ messages.onboarding.refresh }}</p>
    </div>
  </section>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'

import { useGuild } from '@/shared/ports'
import { AppButton } from '@/shared/ui'
import { loginWithDiscord, session } from '@/app/session'
import { useGuildAdminMessages } from './locales'

/**
 * Props for `GuildOnboardingPanel`. Intentionally empty: request state is
 * local to this access-gate instance while authoritative guild state remains
 * in the host GuildPort. Exported per the AGENTS.md "every component exports
 * `XxxProps`" rule so consumers have a canonical type even though the
 * component currently accepts nothing.
 */
export interface GuildOnboardingPanelProps {}

const guild = useGuild()
const messages = useGuildAdminMessages()
const lockedGuilds = computed(() => guild.lockedGuilds.value)
const requestingGuildId = ref<string | null>(null)
const accessRequestError = ref<string | null>(null)

const titleId = 'guild-onboarding-title'

function requestButtonLabel(discordGuildId: string, accessRequested: boolean): string {
  if (accessRequested) return messages.value.onboarding.requestPending
  return requestingGuildId.value === discordGuildId ? messages.value.onboarding.requesting : messages.value.onboarding.request
}

async function requestAccess(discordGuildId: string): Promise<void> {
  if (requestingGuildId.value !== null) return
  requestingGuildId.value = discordGuildId
  accessRequestError.value = null
  try {
    const requested = await guild.requestGuildAccess(discordGuildId)
    if (!requested) {
      accessRequestError.value = messages.value.onboarding.requestFailed
    }
  } catch (cause: unknown) {
    accessRequestError.value = cause instanceof Error ? cause.message : String(cause)
  } finally {
    requestingGuildId.value = null
  }
}

function onLogin(): void {
  loginWithDiscord(guild.activeDiscordGuildId.value)
}
</script>
